import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tabs,
  Tab,
  DialogContentText,
  Tooltip,
  Typography,
  Checkbox,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Contact } from '../../models/contact';
import { useLoadData } from '../../hooks/useLoadData';
import { useFormContext, useWatch } from 'react-hook-form';
import { ApiResponse } from '../../models/api-response';
import { contactApiKeyStore } from '../../api/contact-api-key-store';
import { TabPanel } from '../../components/tab-panel';
import { SiTrackGrid } from '../../components/si-track-grid/si-track-grid';
import { SaveContactComponent } from './save-contact.component';
import { columns } from './grid-columns';
import { QueryKeyConfig } from '../../api/api-helper';
import { SaveAndCancelButton } from '../../components/save-and-cancel-button';
import { LoadingIndicator } from '../../components/loading-indicator.component';
import { ApiErrorComponent } from '../../components/api-error.component';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { useDeleteData } from '../../hooks/useDeleteData';
import { useParams } from 'react-router-dom';
import { UserPermissionName } from '../../models/user-permission-name';
import { useAuthorize } from '../../hooks/use-authorize';
import { fastFieldContactColumn } from './fast-field-contact-column';
import ConfirmDialog from '../../components/confirm-dialog';
import { TableView } from '@mui/icons-material';

interface CellProps {
  moduleData: any;
}

interface CustomGridCellProps {
  field: string;
}

interface ContactsDialogProps {
  open: boolean;
  title?: string;
  contacts: Contact[];
  customerID: string;
  isSyncWithDux?: boolean;
  onClose: (newContacts: Contact[]) => void;
  watchKeys?: string[];
  value: string;
  setValue: (newValue: string) => void;
  editItem: Contact | undefined; // Assuming editItem can be undefined
  setEditItem: (newContact: Contact | undefined) => void;
  getQueryKeyConfig?: (inputData: any, getValues: any) => QueryKeyConfig;
  page?: string;
  pageType?: string;
  handleAddContactFormWithOutClose?: (newContacts: Contact[]) => void;
  itemId?: string;
}

const mergeDataItems = (array1: any[], array2: any[], key: keyof Contact): Contact[] => {
  const mappedArray = new Map<number | string, Contact>(); // Assuming contactsID is a number
  Array.isArray(array1) &&
    array1?.forEach((item) => {
      if (item) {
        mappedArray.set(item[key], item);
      }
    });

  Array.isArray(array2) &&
    array2?.forEach((item) => {
      if (item) {
        mappedArray.set(item[key], item);
      }
    });

  return Array.from(mappedArray.values());
};

export const ContactsDialogWithGridSelect = ({
  open,
  contacts,
  customerID,
  onClose,
  setValue,
  watchKeys,
  value,
  editItem,
  isSyncWithDux,
  setEditItem,
  getQueryKeyConfig,
  page,
  pageType,
  handleAddContactFormWithOutClose,
  itemId,
}: ContactsDialogProps) => {
  const [newContacts, saveNewContacts] = useState<Contact[]>(contacts ?? []);
  const { getValues } = useFormContext();
  const [completeData, setCompleteData] = useState<Contact[]>([]);
  const [currentItem, setCurrentItem] = useState<any>({});
  const [tabAction, setTabAction] = useState(editItem);
  const [updatedProjectContactData, setProjectContactData] = useState<any[]>();
  const [isConfirmation, setIsConfirmation] = useState<boolean>(false);
  const [showConfirmationMessage, setIsConfirmationMessage] = useState<boolean>(false);

  useEffect(() => {
    setTabAction(editItem);
  }, [editItem]);

  const { id, officeID, projectID, unitID } = useParams();
  useWatch({
    name: watchKeys || [],
  });

  const query = getQueryKeyConfig?.(
    watchKeys?.reduce((acc: any, key) => ({ ...acc, [key]: getValues(key) }), {}),
    getValues
  );
  const { data, isLoading } = useLoadData<ApiResponse<Contact[]>>(query, !!query);

  useEffect(() => {
    if (data || newContacts) {
      const mergedContacts: Contact[] = mergeDataItems(data?.data || [], newContacts || [], 'contactsID');
      const updatedData = mergedContacts?.map((contact) => ({
        ...contact,
        isSelected: newContacts?.some((c) => c.contactsID === contact.contactsID) || false,
        isFastFieldCM: contact?.isFastFieldCM,
      }));
      setCompleteData(updatedData ?? []);
      setProjectContactData(updatedData ?? []);
    }
  }, [data, newContacts]);

  const handleClosed = (newContactsData: Contact[], isShowConfirmation: boolean) => {
    if (
      (page === 'office' || page === 'project' || page === 'unit' || page === 'municipality') &&
      isShowConfirmation &&
      showConfirmationMessage &&
      value == 'view'
    ) {
      setIsConfirmation(true);
    } else {
      onClose(newContactsData);
      setEditItem(undefined);
      setIsConfirmationMessage(false);
    }
  };

  const handleSelectedItem = (values: Contact[]) => {
    const data = updatedProjectContactData?.reduce((result: Contact[], service: Contact) => {
      const isSelected = values.some((v) => v.contactsID === service.contactsID);
      return [...result, { ...service, isSelected, isFastFieldCM: isSelected ? service?.isFastFieldCM : false }];
    }, []);
    // saveNewContacts(data?.filter((d: any) => d.isSelected) ?? []);
    setCompleteData(data);
    setProjectContactData(data);
    setIsConfirmationMessage(true);
  };

  const handleSelectSaveTime = (updatedData: Contact[], values: Contact[]) => {
    const data = updatedData.map((d) => ({
      ...d,

      isSelected: values.some((v) => v.contactsID === d.contactsID) || d?.isSelected,
      isFastFieldCM: false,
    }));
    // saveNewContacts(data.filter((d) => d.isSelected));
    setCompleteData(data);
    setProjectContactData(data);
  };

  const handleSaveContactCompleted = (value: Contact) => {
    const updatedContacts = completeData?.filter((contact) => contact?.contactsID !== value?.contactsID);
    setCompleteData([...updatedContacts, value]);
    setProjectContactData([...updatedContacts, value]);
    setEditItem(undefined);

    setValue('view');
    handleSelectSaveTime([...updatedContacts, value], [value]);
    setIsConfirmationMessage(true);
    // if (page === 'customer') {
    //   saveNewContacts(completeData?.filter((d: any) => d.isSelected) ?? []);

    //   handleAddContactFormWithOutClose?.(completeData?.filter((d: any) => d.isSelected) ?? []);
    // }
  };

  const handleEdit = (value: Contact) => {
    setEditItem(value);
    setValue('edit');
    setTabAction(value);
  };

  const handleTabsChange = (event: any, value: any) => {
    if (value == 'view') setTabAction(undefined);
    setValue(value);
  };
  const handleOk = () => {
    saveNewContacts(updatedProjectContactData?.filter((d: any) => d.isSelected) ?? []);
    handleClosed(updatedProjectContactData?.filter((d: any) => d.isSelected) ?? [], false);
  };

  const removeContactItem = (value: any) => {
    if (value?.length > 0) {
      // const updatedContacts = completeData?.filter((contact) => contact?.contactsID !== value[0]?.contactsID);
      const idsToRemove = value?.map((item: Contact) => item.contactsID);
      const updatedContacts = completeData?.filter((contact) => !idsToRemove?.includes(contact.contactsID));
      setCompleteData(updatedContacts);
      setProjectContactData(updatedContacts);
      setCurrentItem(value);
      setIsConfirmationMessage(true);
    }
  };

  const isAddContactPermission = !useAuthorize([UserPermissionName?.ADD_CONTACT]);

  const FastFieldCell = ({ moduleData }: CellProps) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const updatedData = updatedProjectContactData?.map((d: any) => ({
        ...d,
        isFastFieldCM: moduleData?.contactsID == d?.contactsID ? event.target.checked : false,
      }));
      setProjectContactData(updatedData);
      // updateContactList(updatedData);
    };

    return (
      <td>
        <Checkbox
          checked={
            updatedProjectContactData?.find((pl: any) => pl?.contactsID == moduleData?.contactsID)?.isFastFieldCM
          }
          onChange={handleChange}
          inputProps={{ 'aria-label': 'controlled' }}
          disabled={!updatedProjectContactData?.find((pl: any) => pl?.contactsID == moduleData?.contactsID)?.isSelected}
        />
      </td>
    );
  };

  const CustomGridCell = ({ field }: CustomGridCellProps) => {
    if (field === 'isFastFieldCM') {
      return (dataItem: any) => {
        return <FastFieldCell moduleData={dataItem.dataItem} />;
      };
    }
  };

  const handleClose = () => {
    setIsConfirmation(false);
  };
  const handleConfirm = () => {
    setIsConfirmation(false);
    handleClosed(newContacts, false);
  };

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth={'xl'}
      // onClose={handleClosed}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
    >
      <DialogTitle id="dialog-title" sx={{ p: 0, m: 0 }}>
        <Tabs value={value} onChange={handleTabsChange}>
          <Tab value="view" label={<div>VIEW CONTACTS</div>} />
          <Tab value="create" label={<div>CREATE CONTACT</div>} hidden={isAddContactPermission || !!tabAction} />
          <Tab value="edit" label={<div>EDIT CONTACT</div>} hidden={!tabAction} />
        </Tabs>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={
          page !== 'customer' && page !== 'workOrderList'
            ? () => handleClosed(newContacts, true)
            : handleOk
        }
        sx={{
          position: 'absolute',
          right: 10,
          top: 3,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Tooltip title={<Typography variant="body1">Close</Typography>}>
          <CloseIcon />
        </Tooltip>
      </IconButton>
      <DialogContent dividers className="popupDilog">
        <TabPanel selectedValue={value} value="view">
          <SiTrackGrid
            fileTitle='customer_contact'
            dataItems={completeData}
            selectedField={
              page !== 'customer' && page !== 'workOrderList' ? 'isSelected' : undefined
            }
            dataItemKey="contactsID"
            columns={
              page !== 'municipality'
                ? page === 'unit'
                  ? fastFieldContactColumn
                  : columns
                : [
                    ...(columns?.filter((c) => c.field !== 'masterContacts') ?? []),
                    { title: 'Muncipalities', field: 'muncipalityName', filter: 'text', searchable: true },
                  ]
            }
            customCell={(field) =>
              CustomGridCell({
                field,
              })
            }
            onEdit={handleEdit}
            deleteMutationConfig={(item) =>
              contactApiKeyStore.deleteContactMaping(
                item.contactsID,
                !id ? 0 : Number(id),
                !officeID ? 0 : Number(officeID),
                !projectID ? 0 : Number(projectID),
                !unitID ? (!itemId ? 0 : Number(itemId)) : Number(unitID)
              )
            }
            actionPermissions={{
              edit: UserPermissionName.EDIT_CONTACT,
              delete: UserPermissionName.DELETE_CONTACT,
              export: UserPermissionName.DOWNLOAD_GRID,
            }}
            onSelectedItems={handleSelectedItem}
            isBusy={isLoading}
            onDeletedItems={removeContactItem}
          />

          <SaveAndCancelButton
            cancel={{
              onClose:
                page !== 'customer' && page !== 'workOrderList'
                  ? () => handleClosed(newContacts, false)
                  : handleOk,
              showCancel: true,
            }} // No cancelLabel specified; default to "Cancel"
            ok={{ onOk: handleOk, showOk: page !== 'customer' && page !== 'workOrderList' }} // Explicit showOk specified
          />
        </TabPanel>
        <TabPanel selectedValue={value} value="create">
          <SaveContactComponent
            itemId={itemId}
            isSyncWithDux={isSyncWithDux}
            onSaveContact={handleSaveContactCompleted}
            contact={{ customerID }}
            page={page}
            pageType={pageType}
          />
        </TabPanel>
        <TabPanel selectedValue={value} value="edit">
          <SaveContactComponent
            itemId={itemId}
            isSyncWithDux={isSyncWithDux}
            onSaveContact={handleSaveContactCompleted}
            contact={editItem}
            page={page}
            pageType={pageType}
          />
        </TabPanel>
      </DialogContent>
      {isConfirmation && (
        <ConfirmDialog
          message="Please click the OK button to apply the changes before closing."
          onClose={handleClose}
          onConfirm={handleConfirm}
          open={isConfirmation}
          hideButtons
          autoCloseDuration={2200}
        />
      )}
    </Dialog>
  );
};
